import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../../context";
import { setAuthenticatedUser } from "../../auth/service";
import storeApi from "../storeApi";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { trackZbRegistrationComplete } from "../../common/analytics";

/**
 * Zerobuild Store - OTP Verification page
 *
 * Route: /store/verify-otp
 *
 * Responsibilities:
 * - Accept the OTP sent to the user's mobile after registration.
 * - Complete signup by calling POST /api/store/users/register with { mobile, name, profile_type?, company_name? }.
 * - On success, show the confirmation message and redirect to /projects.
 * - Refresh store session (/zb/v1/session) so AppContext.currentUser is populated.
 */
const StoreVerifyOtp = ({
  isOpen = true,
  onClose = null,
  onBackToRegister = null,
  registrationData: registrationDataFromProps = null,
  registerRequest = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser, setDealerReferral, popAuthModalReturnTarget } =
    useContext(AppContext);

  // Try to get registration data from props, location.state, or sessionStorage.
  const registrationData = registrationDataFromProps || location.state || (() => {
    try {
      const stored = window.sessionStorage.getItem("zb_store_registration");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  const initialMobile =
    (location.state && location.state.mobile) ||
    registrationData?.mobile ||
    window.sessionStorage.getItem("zb_store_mobile") ||
    "";
  const initialName = (location.state && location.state.name) || registrationData?.name || "";
  const initialCompanyName = (location.state && location.state.companyName) || registrationData?.companyName || "";
  const initialProjectType = (location.state && location.state.projectType) || registrationData?.projectType || null;
  const initialUserType = (location.state && location.state.userType) || registrationData?.userType || "buyer";

  const [mobile] = useState(initialMobile);
  const [name] = useState(initialName);
  const [companyName] = useState(initialCompanyName);
  const [projectType] = useState(initialProjectType);
  const [userType] = useState(initialUserType);
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const toErrorText = (err, fallback) => {
    const raw =
      err?.message ??
      err?.error ??
      err?.response?.data?.message ??
      err?.response?.data?.error ??
      fallback;
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      if (typeof raw.message === "string") return raw.message;
      if (typeof raw.error === "string") return raw.error;
      try {
        return JSON.stringify(raw);
      } catch {
        return fallback;
      }
    }
    return String(raw ?? fallback);
  };

  useEffect(() => {
    if (!mobile) {
      if (typeof onBackToRegister === "function") {
        onBackToRegister();
        return;
      }
      // Route fallback if modal callbacks are not provided.
      navigate("/register", { replace: true });
    }
  }, [mobile, navigate, onBackToRegister]);

  const canSubmit = otp.trim().length > 0 && !isSubmitting && !!mobile;

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Complete registration using Store::UsersController with OTP
      const body = {
        mobile: mobile.trim(),
        name: name.trim(),
        company_name: companyName.trim(),
        otp_code: otp.trim(),
        profile_type: projectType,
        user_type: userType,
      };

      const registerFn = typeof registerRequest === "function"
        ? registerRequest
        : storeApi.authAPI.registerUser;
      const registerResponse = await registerFn(body);
      const registerRoot = registerResponse || {};
      const registerInner = registerRoot.data || {};
      const registerData = registerInner.data || registerRoot.data?.data || null;
      const becameProspect = Boolean(registerInner?.auth?.became_prospect);
      const registerToken =
        registerData?.token ||
        registerInner.token ||
        registerRoot.token ||
        null;
      const registerUser =
        registerData?.user ||
        registerInner.user ||
        registerRoot.user ||
        null;

      if (registerToken) {
        localStorage.setItem("storeAccessToken", registerToken);
      } else {
        setError("Registration succeeded but token was not returned.");
        return;
      }

      if (registerUser) {
        setAuthenticatedUser({ token: registerToken, user: registerUser });
        setCurrentUser(registerUser);
      }
      trackZbRegistrationComplete();

      // Optional verification: GET /api/store/session.
      // Prefer the freshest user for pre-filling profile form.
      let prefUser = registerUser;
      try {
        const sessionResponse = await storeApi.sessionAPI.getCurrentSession();
        const user =
          sessionResponse?.user ||
          sessionResponse?.data?.user ||
          sessionResponse?.data?.data?.user ||
          null;
        if (user) {
          prefUser = user;
          setAuthenticatedUser({ token: registerToken, user });
          setCurrentUser(user);
        }
        if (typeof setDealerReferral === "function") {
          setDealerReferral(sessionResponse?.dealer_referral || null);
        }
      } catch (sessionError) {
        // Non-fatal: token is already saved; app can still proceed.
        console.warn("Failed to verify store session after register", sessionError);
      }

      setSuccessMessage("Registration successful. Redirecting...");
      setTimeout(() => {
        const dest =
          typeof popAuthModalReturnTarget === "function"
            ? popAuthModalReturnTarget()
            : null;
        if (typeof onClose === "function") onClose();

        if (becameProspect) {
          const buyerProfileType = String(prefUser?.buyer?.profile_type ?? "").trim();
          navigate("/register", {
            replace: true,
            state: {
              postLoginProfile: true,
              mobile: String(prefUser?.mobile ?? mobile).trim(),
              name: String(prefUser?.name ?? "").trim(),
              companyName: String(prefUser?.buyer_name ?? "").trim(),
            projectType: buyerProfileType || "",
          },
        });
          return;
        }

        if (dest?.pathname) {
          navigate(dest.pathname, {
            replace: true,
            ...(dest.state !== undefined ? { state: dest.state } : {}),
          });
        } else {
          navigate("/products", { replace: true });
        }
      }, 2000);
    } catch (e) {
      setError(toErrorText(e, "Invalid OTP. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <>
      <h2 className="mb-3 fw-bold text-center">OTP Verification</h2>
      <p className="text-muted text-center mb-4">
        Enter the verification code sent to <strong>{mobile}</strong>.
      </p>

      <form onSubmit={handleVerify}>
        <div className="mb-3">
          <label className="form-label fw-semibold">OTP</label>
          <input
            type="text"
            className="form-control text-center unmask"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
        </div>

        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success py-2" role="alert">
            {successMessage}
          </div>
        )}

        <div className="d-grid gap-2">
          <button
            type="submit"
            className="btn btn-primary fw-semibold"
            disabled={!canSubmit}
          >
            {isSubmitting ? "Verifying..." : "Verify"}
          </button>
          {typeof onBackToRegister === "function" ? (
            <button
              type="button"
              className="btn btn-link text-decoration-none"
              onClick={onBackToRegister}
            >
              Back to Register
            </button>
          ) : null}
        </div>
      </form>
    </>
  );

  if (typeof onClose === "function") {
    return (
      <Modal isOpen={isOpen} centered={false} toggle={onClose} size="md">
        <ModalHeader toggle={onClose}>Verify OTP</ModalHeader>
        <ModalBody>{content}</ModalBody>
      </Modal>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-5">
          {content}
        </div>
      </div>
    </div>
  );
};

export default StoreVerifyOtp;


